import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  getDocsFromCache,
  addDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  doc,
  runTransaction,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useLimits } from './useLimits';
import { incrementInventoryCount, decrementInventoryCount } from '../services/userRecordsService';
import {
  InventoryFolder,
  createFolder as createFolderService,
  getUserFolders,
  renameFolder as renameFolderService,
  deleteFolder as deleteFolderService,
} from '../services/inventoryFoldersService';
import { LOAN_HISTORY_COLLECTION } from '../services/inventoryLoanHistoryService';

export interface Loan {
  loanId?: string;
  borrowerName: string;
  quantity: number;
  dueDate?: string;
  loanedAt: string;
  borrowerGuestId?: string;
  borrowerGroup?: string;
  borrowerPhone?: string;
  borrowerEmail?: string;
}

export interface InventoryItem {
  id: string;
  itemName: string;
  description?: string;
  photoUrl?: string;
  photoUrl2?: string;
  quantity?: number;
  unitValue?: number;
  brand?: string;
  store?: string;
  category?: string;
  measurements?: string; // Adicionado
  observations?: string;
  loan?: Loan | null;
  folderId?: string;
  userId: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateInventoryItemData {
  itemName: string;
  description?: string;
  photoUrl?: string;
  photoUrl2?: string;
  quantity?: number;
  unitValue?: number;
  brand?: string;
  store?: string;
  category?: string;
  measurements?: string; // Adicionado
  observations?: string;
  folderId?: string;
}

const mapSnapshot = (snapshot: any): InventoryItem[] => {
  const list: InventoryItem[] = [];
  snapshot.forEach((docSnapshot: any) => {
    const data = docSnapshot.data();
    list.push({
      id: docSnapshot.id,
      itemName: data.itemName || '',
      description: data.description,
      photoUrl: data.photoUrl,
      photoUrl2: data.photoUrl2,
      quantity: data.quantity || 1,
      unitValue: data.unitValue,
      brand: data.brand,
      store: data.store,
      category: data.category,
      measurements: data.measurements,
      observations: data.observations,
      loan: data.loan || null,
      folderId: data.folderId,
      userId: data.userId,
      createdAt: data.createdAt?.toDate ? data.createdAt.toDate().toISOString() : new Date().toISOString(),
      updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate().toISOString() : new Date().toISOString(),
    });
  });
  return list;
};

export const useInventory = () => {
  const { user } = useAuth();
  const { checkInventoryLimit } = useLimits();
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [folders, setFolders] = useState<InventoryFolder[]>([]);
  const [foldersLoading, setFoldersLoading] = useState(true);

  // Buscar itens do usuário (sem listener)
  const fetchItems = async () => {
    if (!user?.uid) {
      setItems([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const q = query(
        collection(db, 'inventoryItems'),
        where('userId', '==', user.uid),
        orderBy('createdAt', 'desc')
      );

      try {
        const cached = await getDocsFromCache(q);
        if (!cached.empty) {
          setItems(mapSnapshot(cached));
          setLoading(false);
        }
      } catch { void 0; }

      const snapshot = await getDocs(q);
      setItems(mapSnapshot(snapshot));
      setError(null);
    } catch (err: any) {
      setError('Erro ao carregar acervo');
    } finally {
      setLoading(false);
    }
  };

  const fetchFolders = async () => {
    if (!user?.uid) {
      setFolders([]);
      setFoldersLoading(false);
      return;
    }

    try {
      setFoldersLoading(true);
      const userFolders = await getUserFolders(user.uid);
      setFolders(userFolders);
    } catch (err: any) {
      setFolders([]);
    } finally {
      setFoldersLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    fetchFolders();
  }, [user?.uid]);

  const createFolder = async (name: string): Promise<InventoryFolder> => {
    if (!user?.uid) {
      throw new Error('Usuário não autenticado');
    }
    const folder = await createFolderService(user.uid, name);
    setFolders(prev => [...prev, folder].sort((a, b) => a.name.localeCompare(b.name)));
    return folder;
  };

  const renameFolder = async (folderId: string, name: string): Promise<void> => {
    await renameFolderService(folderId, name);
    setFolders(prev =>
      prev
        .map(folder => (folder.id === folderId ? { ...folder, name: name.trim() } : folder))
        .sort((a, b) => a.name.localeCompare(b.name))
    );
  };

  const deleteFolder = async (folderId: string): Promise<void> => {
    if (!user?.uid) {
      throw new Error('Usuário não autenticado');
    }
    await deleteFolderService(folderId, user.uid);
    setFolders(prev => prev.filter(folder => folder.id !== folderId));
    await fetchItems();
  };

  // Criar novo item
  const createItem = async (itemData: CreateInventoryItemData): Promise<InventoryItem> => {
    if (!user?.uid) {
      throw new Error('Usuário não autenticado');
    }

    try {
      const limitCheck = await checkInventoryLimit();
      if (!limitCheck.allowed) {
        throw new Error(limitCheck.message || 'Limite de itens atingido');
      }

      const cleanedData: any = {
        itemName: itemData.itemName,
        userId: user.uid,
        quantity: itemData.quantity || 1,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      if (itemData.description) cleanedData.description = itemData.description;
      if (itemData.photoUrl) cleanedData.photoUrl = itemData.photoUrl;
      if (itemData.photoUrl2) cleanedData.photoUrl2 = itemData.photoUrl2;
      if (itemData.unitValue !== undefined) cleanedData.unitValue = itemData.unitValue;
      if (itemData.brand) cleanedData.brand = itemData.brand;
      if (itemData.store) cleanedData.store = itemData.store;
      if (itemData.category) cleanedData.category = itemData.category;
      if (itemData.measurements) cleanedData.measurements = itemData.measurements;
      if (itemData.observations) cleanedData.observations = itemData.observations;
      if (itemData.folderId) cleanedData.folderId = itemData.folderId;

      const docRef = await addDoc(collection(db, 'inventoryItems'), cleanedData);

      try {
        await incrementInventoryCount(user.uid);
      } catch (countError) {
        // Silently handle error
      }

      const createdItem: InventoryItem = {
        id: docRef.id,
        itemName: itemData.itemName,
        description: itemData.description,
        photoUrl: itemData.photoUrl,
        photoUrl2: itemData.photoUrl2,
        quantity: itemData.quantity || 1,
        unitValue: itemData.unitValue,
        brand: itemData.brand,
        store: itemData.store,
        category: itemData.category,
        measurements: itemData.measurements,
        observations: itemData.observations,
        folderId: itemData.folderId,
        userId: user.uid,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      await fetchItems();

      return createdItem;
    } catch (err: any) {
      if (err?.code === 'permission-denied') {
        throw new Error('Limite do seu plano atingido para itens do acervo. Faça upgrade para continuar.');
      }
      throw new Error(err.message || 'Erro ao criar item no acervo');
    }
  };

  // Atualizar item
  const updateItem = async (itemId: string, updates: Partial<CreateInventoryItemData>): Promise<void> => {
    if (!user?.uid) {
      throw new Error('Usuário não autenticado');
    }

    try {
      // Remover campos undefined do updates
      const cleanedUpdates = Object.entries(updates).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {} as Record<string, any>);

      if ('folderId' in updates) {
        cleanedUpdates.folderId = updates.folderId ? updates.folderId : deleteField();
      }

      const itemRef = doc(db, 'inventoryItems', itemId);
      await updateDoc(itemRef, {
        ...cleanedUpdates,
        updatedAt: Timestamp.now(),
      });

      // Atualizar lista local
      await fetchItems();
    } catch (err: any) {
      console.error('Erro ao atualizar item:', err);
      throw new Error('Erro ao atualizar item');
    }
  };

  const generateLoanId = (): string =>
    `ln_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

  const setItemLoan = async (itemId: string, loan: Loan): Promise<void> => {
    if (!user?.uid) {
      throw new Error('Usuário não autenticado');
    }
    const itemRef = doc(db, 'inventoryItems', itemId);
    try {
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(itemRef);
        if (!snap.exists()) throw new Error('Item não encontrado');
        const current = snap.data() as any;
        if (current.loan?.borrowerName) throw new Error('Este item já está emprestado.');
        const loanId = loan.loanId || generateLoanId();
        const loanWithId: Loan = { ...loan, loanId };
        tx.update(itemRef, { loan: loanWithId, updatedAt: Timestamp.now() });
        const record: any = {
          loanId,
          itemId,
          ownerId: user.uid,
          borrowerName: loan.borrowerName,
          quantity: loan.quantity,
          loanedAt: loan.loanedAt,
          status: 'active',
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        };
        if (loan.borrowerGuestId) record.borrowerGuestId = loan.borrowerGuestId;
        if (loan.borrowerGroup) record.borrowerGroup = loan.borrowerGroup;
        if (loan.dueDate) record.dueDate = loan.dueDate;
        tx.set(doc(db, LOAN_HISTORY_COLLECTION, loanId), record);
      });
      await fetchItems();
    } catch (err: any) {
      console.error('Erro ao registrar empréstimo:', err);
      const known = ['Este item já está emprestado.', 'Item não encontrado'];
      throw new Error(known.includes(err?.message) ? err.message : 'Erro ao registrar empréstimo');
    }
  };

  const returnItem = async (
    itemId: string,
    expected?: { loanId?: string; loanedAt?: string; borrowerName?: string }
  ): Promise<void> => {
    if (!user?.uid) {
      throw new Error('Usuário não autenticado');
    }
    const itemRef = doc(db, 'inventoryItems', itemId);
    try {
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(itemRef);
        if (!snap.exists()) return;
        const current = snap.data() as any;
        const loan = current.loan;
        if (loan?.borrowerName) {
          const matches = !!expected && (
            loan.loanId && expected.loanId
              ? loan.loanId === expected.loanId
              : loan.loanedAt === expected.loanedAt && loan.borrowerName === expected.borrowerName
          );
          if (!matches) throw new Error('LOAN_CHANGED');
        }
        tx.update(itemRef, { loan: null, updatedAt: Timestamp.now() });
        if (loan?.borrowerName) {
          const loanId = loan.loanId || generateLoanId();
          const record: any = {
            loanId,
            itemId,
            ownerId: user.uid,
            borrowerName: loan.borrowerName,
            quantity: loan.quantity,
            loanedAt: loan.loanedAt,
            returnedAt: new Date().toISOString(),
            status: 'returned',
            updatedAt: Timestamp.now(),
          };
          if (loan.borrowerGuestId) record.borrowerGuestId = loan.borrowerGuestId;
          if (loan.borrowerGroup) record.borrowerGroup = loan.borrowerGroup;
          if (loan.dueDate) record.dueDate = loan.dueDate;
          tx.set(doc(db, LOAN_HISTORY_COLLECTION, loanId), record, { merge: true });
        }
      });
      await fetchItems();
    } catch (err: any) {
      console.error('Erro ao registrar devolução:', err);
      throw new Error(
        err?.message === 'LOAN_CHANGED'
          ? 'O empréstimo deste item mudou. Recarregue a página antes de devolver.'
          : 'Erro ao registrar devolução'
      );
    }
  };

  // Deletar item
  const deleteItem = async (itemId: string): Promise<void> => {
    if (!user?.uid) {
      throw new Error('Usuário não autenticado');
    }

    try {
      await deleteDoc(doc(db, 'inventoryItems', itemId));

      await decrementInventoryCount(user.uid);

      setItems(prevItems => prevItems.filter(item => item.id !== itemId));
    } catch (err: any) {
      throw new Error('Erro ao deletar item');
    }
  };

  // Buscar item por ID
  const getItemById = (itemId: string): InventoryItem | undefined => {
    return items.find(item => item.id === itemId);
  };

  // Filtrar por categoria
  const getItemsByCategory = (category: string): InventoryItem[] => {
    return items.filter(item => item.category === category);
  };

  // Buscar itens
  const searchItems = (searchTerm: string): InventoryItem[] => {
    const term = searchTerm.toLowerCase();
    return items.filter(item =>
      item.itemName.toLowerCase().includes(term) ||
      item.description?.toLowerCase().includes(term) ||
      item.brand?.toLowerCase().includes(term) ||
      item.category?.toLowerCase().includes(term)
    );
  };

  // Estatísticas
  const getStats = () => {
    const totalItems = items.length;
    const totalValue = items.reduce((sum, item) => {
      return sum + ((item.unitValue || 0) * (item.quantity || 1));
    }, 0);

    const categoriesCount = items.reduce((acc, item) => {
      const category = item.category || 'Sem categoria';
      acc[category] = (acc[category] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      totalItems,
      totalValue,
      categoriesCount,
      categories: Object.keys(categoriesCount),
    };
  };

  // Buscar todos os itens do inventário
  const getAllItems = (): InventoryItem[] => {
    return items;
  };

  return {
    // Estado
    items,
    loading,
    error,

    folders,
    foldersLoading,
    createFolder,
    renameFolder,
    deleteFolder,
    refreshFolders: fetchFolders,

    // Ações CRUD
    createItem,
    updateItem,
    deleteItem,
    setItemLoan,
    returnItem,

    // Busca e filtros
    getItemById,
    getItemsByCategory,
    searchItems,

    // Estatísticas
    getStats,

    // Refresh manual
    refreshItems: fetchItems,

    // Obter todos os itens
    getAllItems, // Adicionar o método ao retorno
  };
};
